/**
 * The queries behind a reference field (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter):
 * rows of the target table by a typed fragment of its display column, and one
 * row by its key. Both go through the SQL firewall as the signed-in user, so a
 * picker shows exactly the rows the person may read.
 */
import { adminRpc } from "../../../services/admin-rpc-client";

export type ReferenceTarget = {
  /** the logical table the firewall serves: "job" */
  table: string;
  /** its key column: "uid" */
  key: string;
  /** the column a person reads a row by: "name" */
  display: string;
  /** the columns a looked-up row carries (default: key and display) */
  columns?: string[];
};

export type ReferenceValue = {
  key: string;
  text: string;
  /** the whole target row, when it was looked up — what a page reads through the pick (`job.name`, `job.worker_uid`) */
  row?: Record<string, unknown>;
};

/** How many rows a search returns; the person narrows by typing. */
export const SEARCH_LIMIT = 50;

const lit = (v: string) => `'${v.replace(/'/g, "''")}'`;

/** The rows matching `term` (any part of the display column, case-folded), SEARCH_LIMIT of them, in display order. */
export async function searchReference(target: ReferenceTarget, term: string): Promise<ReferenceValue[]> {
  const where = term.trim() ? ` WHERE lower(${target.display}) LIKE lower(${lit(`%${term.trim()}%`)})` : "";
  const sql = `SELECT ${target.key}, ${target.display} FROM ${target.table}${where} ORDER BY ${target.display} LIMIT ${SEARCH_LIMIT}`;
  const r = await adminRpc.query(sql);
  return (r.rows ?? []).map((row) => ({ key: String(row[target.key]), text: String(row[target.display] ?? row[target.key]) }));
}

/**
 * The one row with this key, whole: a pick stands for a selected row of the
 * target, so what a grid selection would carry (every column) the pick
 * carries too — the route brought a key; the page may read `job.name`.
 */
export async function lookupReference(target: ReferenceTarget, key: string): Promise<ReferenceValue | undefined> {
  // Named columns, never `*`: through the firewall `*` can carry an auto-join's columns under the base
  // table's names (job.name shadowed by worker.name), and the display column may be calculated.
  const cols = [...new Set([target.key, target.display, ...(target.columns ?? [])])].join(", ");
  const r = await adminRpc.query(`SELECT ${cols} FROM ${target.table} WHERE ${target.key} = ${lit(key)} LIMIT 1`);
  const row = r.rows?.[0];
  return row ? { key: String(row[target.key]), text: String(row[target.display] ?? row[target.key]), row } : undefined;
}
